import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Sparkles, Tag, TrendingUp, X, Plus, Gift } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GradButton } from "@/components/erp";
import { DUYURU_TIP } from "@/lib/perms";
import api from "@/lib/api";

const IKON = { yeni_urun: Sparkles, kampanya: Tag, fiyat: TrendingUp, genel: Megaphone };
const RENK = { yeni_urun: "text-emerald-300", kampanya: "text-rose-300", fiyat: "text-amber-300", genel: "text-sky-300" };

const Icerik = ({ d, onEkle }) => {
  const Icon = IKON[d.tip] || Megaphone;
  return (
    <div className="flex gap-4 items-start min-w-0 flex-1">
      <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${RENK[d.tip]}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3"><span className={`text-[10px] uppercase tracking-[0.2em] ${RENK[d.tip]}`}>{DUYURU_TIP[d.tip]}</span><span className="font-medium">{d.baslik}</span>
          {d.ozel && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300 inline-flex items-center gap-1" data-testid={`bayi-duyuru-ozel-${d.id}`}><Gift className="w-3 h-3" />Size özel</span>}
          {d.indirim_yuzde > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300" data-testid={`bayi-duyuru-indirim-${d.id}`}>%{d.indirim_yuzde} indirim{d.bitis ? ` · ${new Date(d.bitis).toLocaleDateString("tr-TR")} tarihine kadar` : ""}</span>}</div>
        {d.metin && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{d.metin}</p>}
        {d.urunler?.length > 0 && <div className="flex flex-wrap gap-2 mt-2">{d.urunler.map((u) => (
          <button key={u.id} data-testid={`bayi-duyuru-urun-${u.kod}`} title={`${u.ad} — sepete 1 adet ekle`} onClick={() => onEkle(u, 1)}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-mono bg-white/10 hover:bg-white/15"><Plus className="w-3 h-3" />{u.kod}</button>))}</div>}
      </div>
    </div>
  );
};

const AcilisPencere = ({ list, onEkle, onKapat }) => (
  <Dialog open onOpenChange={(v) => !v && onKapat()}>
    <DialogContent className="glass max-w-2xl max-h-[85vh] overflow-y-auto erp-scroll" data-testid="bayi-duyuru-pencere">
      <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Megaphone className="w-5 h-5 text-[var(--g1)]" />Size yeni {list.length > 1 ? `${list.length} duyuru` : "bir duyuru"} var</DialogTitle></DialogHeader>
      <div className="space-y-3">{list.map((d) => <div key={d.id} className="rounded-xl border border-white/10 p-4" data-testid={`bayi-duyuru-pencere-${d.id}`}><Icerik d={d} onEkle={onEkle} /></div>)}</div>
      <div className="flex justify-end"><GradButton data-testid="bayi-duyuru-okudum" onClick={onKapat}>Okudum</GradButton></div>
    </DialogContent>
  </Dialog>
);

export const BayiDuyurular = ({ userId, onEkle }) => {
  const anahtar = `bayi-duyuru-kapali-${userId}`;
  const [list, setList] = useState([]);
  const [pencere, setPencere] = useState(null);
  const [kapali, setKapali] = useState(() => { try { return JSON.parse(localStorage.getItem(anahtar)) || []; } catch { return []; } });
  const [tumu, setTumu] = useState(false);
  const bilinen = useRef(null);
  const getir = useCallback(() => api.get("/bayi/duyurular").then((r) => {
    const yeni = r.data.filter((d) => !d.okundu);
    if (bilinen.current === null) { if (yeni.length) setPencere(yeni); }
    else yeni.filter((d) => !bilinen.current.has(d.id)).forEach((d) => toast(d.baslik, { description: d.metin?.slice(0, 120), duration: 12000, action: { label: "Gör", onClick: () => setPencere([d]) } }));
    bilinen.current = new Set(r.data.map((d) => d.id));
    setList(r.data);
  }).catch(() => {}), []);
  useEffect(() => {
    getir();
    const t = setInterval(getir, 120000);
    return () => clearInterval(t);
  }, [getir]);
  const okundu = () => { (pencere || []).forEach((d) => api.post(`/bayi/duyurular/${d.id}/okundu`).catch(() => {})); setPencere(null); };
  const kapat = (id) => { const k = [...kapali, id]; setKapali(k); localStorage.setItem(anahtar, JSON.stringify(k)); };
  const hepsi = list.filter((d) => !kapali.includes(d.id));
  const goster = tumu ? hepsi : hepsi.slice(0, 2);
  return (
    <>
      {pencere && <AcilisPencere list={pencere} onEkle={onEkle} onKapat={okundu} />}
      {goster.length > 0 && <div className="space-y-2" data-testid="bayi-duyurular">
        {goster.map((d, i) => (
          <div key={d.id} className="glass rounded-2xl px-5 py-4 flex gap-4 items-start border border-white/10 animate-in fade-in slide-in-from-top-2" style={{ animationDelay: `${i * 80}ms` }} data-testid={`bayi-duyuru-${d.id}`}>
            <Icerik d={d} onEkle={onEkle} />
            <button data-testid={`bayi-duyuru-kapat-${d.id}`} onClick={() => kapat(d.id)} title="Kapat" className="p-1.5 rounded-full text-muted-foreground hover:text-white hover:bg-white/10"><X className="w-4 h-4" /></button>
          </div>))}
        {hepsi.length > 2 && <button data-testid="bayi-duyuru-tumu" onClick={() => setTumu(!tumu)} className="text-xs text-muted-foreground hover:text-white px-2">{tumu ? "Daha az göster" : `Tüm duyuruları gör (${hepsi.length})`}</button>}
      </div>}
    </>
  );
};
